import type { ReportT } from '@/features/Report/types';
import { v1ApiClient } from '@/services';
import { createFeatureApiConfig } from '@/services/apiMockConfig';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { mockReportService } from './mock';
import type {
  FetchReportListParams,
  ReportCreatePayloadDTO,
} from './types';

// Real API service
const realReportService = {
  fetchReportList: async (
    params?: FetchReportListParams,
  ): Promise<{
    data: ReportT[];
    count: number;
  }> => {
    const {
      limit = 10,
      offset = 0,
      order_by = 'desc',
      sort_by = 'updated_at',
    } = params || {};

    const wa = getCurrWorkspaceAlias();
    const response = await v1ApiClient.get(`/workspaces/${wa}/reports`, {
      params: {
        limit,
        offset,
        order_by,
        sort_by,
      },
    });
    return response.data;
  },
  fetchReport: async (alias: string, isPublic?: boolean): Promise<ReportT> => {
    const url = isPublic ? `/reports/${alias}/public` : `/reports/${alias}`;
    const response = await v1ApiClient.get(url);
    return response.data;
  },
  createReport: async (data: ReportCreatePayloadDTO): Promise<ReportT> => {
    const wa = getCurrWorkspaceAlias();
    const response = await v1ApiClient.post(`/workspaces/${wa}/reports`, data);
    return response.data;
  },
  async fetchChartTable(
    report_alias: string,
    chart_alias: string,
    isPublic?: boolean,
  ): Promise<ChartTableRes> {
    const url = isPublic
      ? `/reports/${report_alias}/charts/${chart_alias}/table/public`
      : `/reports/${report_alias}/charts/${chart_alias}/table`;
    const response = await v1ApiClient.get(url);
    return response.data;
  },
};

export const reportApiConfig = createFeatureApiConfig('report', {
  defaultUseMock: false,
  enableConsoleHelpers: true,
});

// Export the service based on configuration
export const reportService = reportApiConfig.isMockMode()
  ? {
      ...realReportService,
      ...mockReportService,
    }
  : realReportService;

// Export both services for manual switching if needed
export { mockReportService, realReportService };
